'use client'
import React, {useEffect, useState} from 'react'
import {motion, AnimatePresence} from 'framer-motion'

import AmyButton from '@/components/UI/AmyButton/AmyButton'
import Box from '@/components/UI/Box/Box'
import {Body5, Body7} from '@/components/UI/Typography'

import CookieModal from './CookieModal'

import useConsentManager from '@/src/shared/lib/hooks/useConsentManager'

const CookieSettings = () => {
  const {consent, updateConsent, isLoading, error, clearError} =
    useConsentManager()
  const [open, setOpen] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  const saveCookieConsent = (check: boolean) => {
    updateConsent(check)
    setOpen(false)
  }

  useEffect(() => {
    if (consent === null) {
      setTimeout(() => setIsVisible(true), 500)
    } else {
      setIsVisible(false)
    }
  }, [consent])

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        clearError()
      }, 5000)

      return () => clearTimeout(timer)
    }
  }, [error, clearError])

  const containerVariants = {
    hidden: {
      y: '100%',
      opacity: 0,
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: 'easeOut' as const,
      },
    },
    exit: {
      y: '100%',
      opacity: 0,
      transition: {
        duration: 0.3,
        ease: 'easeIn' as const,
      },
    },
  }

  return (
    <>
      <AnimatePresence>
        {consent === null && isVisible && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed bottom-0 left-0 w-full z-[40] p-4 sm:p-6">
            <Box className="bg-gradient-to-r bg-primary backdrop-blur-lg rounded-2xl shadow-2xl border border-white/10 p-6 sm:p-8">
              <Box className="flex-col sm:flex-row items-center justify-between gap-6 sm:gap-8">
                <Box className="flex-col gap-3 sm:gap-4 flex-1">
                  <Body5 className="text-white font-bold leading-tight">
                    Sana daha iyi bir online danışmanlık deneyimi sunmak için
                    çerezleri kullanıyoruz.
                  </Body5>
                  <Body7 className="text-white/90 leading-relaxed">
                    Advicemy olarak, güvenilir ve gelişmiş bir online
                    danışmanlık deneyimi sunmak amacıyla çerezleri kullanıyoruz.
                    Onaylıyorum seçeneğine tıklayarak bu çerezlerin kullanımına
                    izin verebilirsin. İnternet sitemizde kullanılan çerezler
                    hakkında daha detaylı bilgi için Çerez Politikamızı
                    inceleyebilirsiniz. Onaylamanız halinde çerezler kapsamında
                    işlenen kişisel verileriniz çerezin çalışması için gerekli
                    olduğu ölçüde yurt dışına aktarılacaktır.
                  </Body7>
                </Box>

                <Box className="flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
                  <button
                    onClick={() => setOpen(true)}
                    className="text-white px-6 py-3 text-sm font-medium underline underline-offset-4 hover:underline-offset-8 transition-all duration-300 hover:text-white/80 rounded-lg hover:bg-white/10 backdrop-blur-sm">
                    Ayarlar
                  </button>

                  <AmyButton
                    onPress={() => saveCookieConsent(true)}
                    className="bg-white text-primary font-semibold px-8 py-3 rounded-xl hover:bg-gray-50 hover:shadow-lg transition-all duration-300 min-w-[120px]"
                    size="md"
                    isLoading={isLoading}
                    isDisabled={isLoading}>
                    {isLoading ? 'Kaydediliyor...' : 'Kabul Et'}
                  </AmyButton>
                </Box>
              </Box>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
      <CookieModal
        open={open}
        saveCookieConsent={saveCookieConsent}
        setOpen={setOpen}
      />
    </>
  )
}

export default CookieSettings
